/**
 * Passphrase Entropy Estimation
 *
 * Conservative entropy calculation that assumes attackers know:
 * - The wordlist size (4096 words from EFF/BIP39-style lists)
 * - Common emoji usage patterns (~256 commonly used emoji)
 * - Connector words (FILLER tokens) add minimal entropy
 *
 * This module gives users realistic security estimates while encouraging
 * strong passphrases through education rather than false confidence.
 */

import { TokenType, Locale, tokenizeAndClassify } from './classifyToken';

/**
 * Size of assumed wordlist for entropy calculation.
 * Based on EFF long wordlist and BIP39 standard (2048-4096 words).
 */
export const WORDLIST_SIZE = 4096;

/**
 * Assumed size of emoji character space for entropy calculation.
 * Conservative estimate of commonly used emoji (actual Unicode emoji > 3000).
 */
export const EMOJI_SET_SIZE = 256;

/**
 * Common short words that appear frequently and should be penalized.
 * These are often used as connectors or have low information content.
 */
const COMMON_SHORT_WORDS = new Set([
  'is', 'my', 'me', 'it', 'he', 'we', 'up', 'go', 'no', 'so',
  'cat', 'dog', 'car', 'run', 'red', 'big', 'yes', 'new', 'old', 'see',
  'the', 'and', 'are', 'you', 'for', 'was', 'all', 'but', 'not', 'can',
  'mi', 'tu', 'el', 'la', 'un', 'si', 'no', 'es', 'su', 'ya',
  'gato', 'casa', 'año', 'dia', 'mes', 'vez', 'dos', 'tan', 'muy', 'mas'
]);

/**
 * Common phrase patterns that should be penalized (language-agnostic).
 * These are frequently used constructions that reduce entropy.
 */
const COMMON_PHRASE_PATTERNS: Array<{ words: string[]; penalty: number }> = [
  { words: ['my', 'cat'], penalty: 10 },
  { words: ['my', 'dog'], penalty: 10 },
  { words: ['my', 'name'], penalty: 10 },
  { words: ['is', 'named'], penalty: 10 },
  { words: ['mi', 'gato'], penalty: 10 },
  { words: ['mi', 'perro'], penalty: 10 },
  { words: ['mi', 'nombre'], penalty: 10 },
  { words: ['se', 'llama'], penalty: 10 },
];

/**
 * Estimate the entropy contribution of a single token.
 *
 * @param token - The token string
 * @param type - The classified token type
 * @param locale - Language locale for context
 * @returns Entropy in bits (conservative estimate)
 *
 * Calculation rationale:
 * - EMOJI: log2(256) ≈ 8 bits (common emoji subset)
 * - WORD: log2(4096) ≈ 12 bits baseline, with penalties for:
 *   - Common short words: -6 bits
 *   - Name-like tokens (FirstCaps/ALLCAPS): -6 bits
 * - DIGIT: 3.32 bits per digit (log2(10) ≈ 3.32)
 * - CHAR: 4 bits (conservative estimate for symbols)
 * - FILLER: 0 bits (connectors add no security)
 *
 * @example
 * estimateTokenEntropy('🐈', 'EMOJI', 'en') // ~8 bits
 * estimateTokenEntropy('cat', 'WORD', 'en') // ~6 bits (common short word)
 * estimateTokenEntropy('Y', 'FILLER', 'es') // 0 bits
 * estimateTokenEntropy('123', 'DIGIT', 'en') // ~9.96 bits (3 digits)
 */
export function estimateTokenEntropy(
  token: string,
  type: TokenType,
  locale: Locale
): number {
  switch (type) {
    case 'EMOJI':
      // log2(256) = 8 bits per emoji
      return Math.log2(EMOJI_SET_SIZE);

    case 'WORD': {
      // Baseline: log2(4096) = 12 bits
      let bits = Math.log2(WORDLIST_SIZE);

      const lowerToken = token.toLowerCase();

      // Penalty for common short words (2-3 letters)
      if (token.length <= 3 && COMMON_SHORT_WORDS.has(lowerToken)) {
        bits -= 6;
      }

      // Penalty for name-like tokens (FirstCaps or ALLCAPS with only letters)
      const isAllCaps = token === token.toUpperCase() && /^[A-Z]+$/.test(token);
      const isFirstCaps = token[0] === token[0].toUpperCase() &&
                          token.slice(1) === token.slice(1).toLowerCase() &&
                          /^[A-Za-z]+$/.test(token);

      if ((isAllCaps || isFirstCaps) && token.length > 1) {
        bits -= 6;
      }

      // Ensure non-negative entropy
      return Math.max(0, bits);
    }

    case 'DIGIT': {
      // Each digit contributes log2(10) ≈ 3.32 bits
      const digitCount = token.length;
      return digitCount * Math.log2(10);
    }

    case 'CHAR':
      // Conservative estimate for special characters
      return 4;

    case 'FILLER':
      // Connectors add zero entropy (attacker assumes their presence)
      return 0;

    default:
      return 0;
  }
}

/**
 * Estimate the total entropy of a passphrase with pattern detection.
 *
 * @param passphrase - The passphrase to analyze
 * @param locale - Language locale for filler detection
 * @returns Object containing total bits and detailed token breakdown
 *
 * The function:
 * 1. Tokenizes and classifies the passphrase
 * 2. Calculates per-token entropy
 * 3. Applies penalties for common phrase patterns
 * 4. Returns total entropy (rounded) and token details
 *
 * @example
 * estimatePassphraseEntropy('my cat 🐈', 'en')
 * // {
 * //   bits: 14, // (6 + 6 + 8 - 10 penalty for "my cat")
 * //   tokens: [
 * //     { value: 'my', type: 'WORD', bits: 6 },
 * //     { value: 'cat', type: 'WORD', bits: 6 },
 * //     { value: '🐈', type: 'EMOJI', bits: 8 }
 * //   ]
 * // }
 */
export function estimatePassphraseEntropy(
  passphrase: string,
  locale: Locale
): {
  bits: number;
  tokens: Array<{ value: string; type: TokenType; bits: number }>;
} {
  // Tokenize and classify
  const classified = tokenizeAndClassify(passphrase, locale);

  // Calculate entropy per token
  const tokens = classified.map(({ value, type }) => ({
    value,
    type,
    bits: estimateTokenEntropy(value, type, locale),
  }));

  // Sum base entropy
  let totalBits = tokens.reduce((sum, token) => sum + token.bits, 0);

  // Apply pattern penalties for common phrases
  const lowerValues = tokens.map(t => t.value.toLowerCase());

  for (const pattern of COMMON_PHRASE_PATTERNS) {
    // Look for consecutive matching words
    for (let i = 0; i <= lowerValues.length - pattern.words.length; i++) {
      const slice = lowerValues.slice(i, i + pattern.words.length);
      const matches = pattern.words.every((word, idx) => slice[idx] === word);

      if (matches) {
        totalBits -= pattern.penalty;
        // Only penalize once per pattern instance
        break;
      }
    }
  }

  // Ensure non-negative entropy
  totalBits = Math.max(0, totalBits);

  return {
    bits: Math.round(totalBits),
    tokens,
  };
}
